import type * as ents from '@attain-apps/entities'
import type * as router from '@attain-apps/fe/web/main/lib/router'
import * as store from '@attain-apps/fe/web/main/lib/store'

import { QueryParam } from '../../../query-param'
import type * as me from '../../me'

import type { Feature } from './feature'
import * as models from './models'

export class Selectors {
  /** Indicates whether the scenario dropdown overlay should be rendered. */
  public readonly shouldPresentOverlay
  /** Scenarios available to the current route-bound portal app. */
  public readonly availableScenarios
  /** Currently selected scenario for the active portal app. */
  public readonly currentScenario
  /** ID of the currently selected scenario. */
  public readonly currentScenarioId
  /** Display name for the currently selected scenario. */
  public readonly currentScenarioName
  /** Indicates whether the active app has any scenarios to display. */
  public readonly hasScenarios
  /** Available scenarios grouped into dropdown visibility sections. */
  public readonly sections
  /** Builds a router link for the given scenario. */
  public readonly getUrlForScenario
  /** Checks whether a visibility section is expanded. */
  public readonly isSectionExpanded
  /** Current scenario create/update dialog state. */
  public readonly editDialog
  /** Current scenario draft being edited. */
  public readonly editingScenarioDraft
  /** Indicates whether the draft dialog is creating a new scenario. */
  public readonly isCreatingScenario
  /** Indicates whether the draft dialog is currently submitting. */
  public readonly isSubmittingEdit
  /** Last error from the current scenario create/update dialog. */
  public readonly editError
  /** Current scenario delete confirmation state. */
  public readonly deleteDialog
  /** Scenario selected for delete confirmation. */
  public readonly scenarioMarkedForDeletion
  /** Indicates whether a delete mutation is currently submitting. */
  public readonly isSubmittingDelete
  /** Indicates whether the dropdown-owned scenario list is refreshing. */
  public readonly isRefreshingScenarios
  /** Last failed dropdown-owned scenario list refresh error. */
  public readonly refreshScenariosError
  /** Returns visibility sections other than the scenario's current section. */
  public readonly getOtherSectionsForScenario

  constructor(
    feature: Feature,
    meFeature: me.Feature,
    routerFeature: router.Router,
  ) {
    this.shouldPresentOverlay = feature.state.project(
      state => !!state.presentOverlay,
    )
    this.availableScenarios = feature.state.project(
      meFeature.contexts.selectors.availableScenarios.asSelector,
      (state, scenariosFromMe) => state.scenarios ?? scenariosFromMe,
    )
    this.currentScenario = feature.state.project(
      routerFeature.selectors.getQueryParam(QueryParam.ScenarioAlias)
        .asSelector,
      meFeature.contexts.selectors.scenario.asSelector,
      (state, scenarioAlias, scenarioFromMe) => {
        if (state.scenarios === undefined) {
          return scenarioFromMe
        }

        return state.scenarios.find(
          scenario => scenario.alias === scenarioAlias,
        )
      },
    )
    this.currentScenarioId = this.currentScenario.project(
      scenario => scenario?.id,
    )
    this.currentScenarioName = this.currentScenario.project(
      scenario => scenario?.name ?? 'Nothing selected...',
    )
    this.hasScenarios = this.availableScenarios.project(
      scenarios => scenarios.length > 0,
    )
    this.sections = this.availableScenarios.project(scenarios =>
      models.Section.from(scenarios),
    )
    this.getUrlForScenario = feature.createSelectorMethod(
      (scenario: ents.core.admin.Scenario) =>
        routerFeature.selectors.getUrlWith({
          queryParams: {
            plain: {
              [QueryParam.ScenarioAlias]: scenario.alias,
            },
          },
        }),
    )
    this.isSectionExpanded = feature.createSelectorMethod(
      ({
        section,
        sectionId,
      }: ExactlyOneOf<{
        section: models.Section
        sectionId: models.Section.Id
      }>) => {
        sectionId = sectionId ?? section?.id

        return feature.state.project(state =>
          sectionId !== undefined
            ? state.sectionVisibilityLookup[sectionId]
            : false,
        )
      },
    )
    this.editDialog = feature.state.project(state => state.editDialog)
    this.editingScenarioDraft = this.editDialog.project(
      editDialog => editDialog?.draft,
    )
    this.isCreatingScenario = this.editDialog.project(
      editDialog => editDialog?.mode === 'create',
    )
    this.isSubmittingEdit = this.editDialog.project(
      editDialog => editDialog?.isSubmitting ?? false,
    )
    this.editError = this.editDialog.project(editDialog => editDialog?.error)
    this.deleteDialog = feature.state.project(state => state.deleteDialog)
    this.scenarioMarkedForDeletion = this.deleteDialog.project(
      this.availableScenarios.asSelector,
      (deleteDialog, scenarios) =>
        scenarios.find(scenario => scenario.id === deleteDialog?.scenarioId),
    )
    this.isSubmittingDelete = this.deleteDialog.project(
      deleteDialog => deleteDialog?.isSubmitting ?? false,
    )
    this.isRefreshingScenarios = feature.state.project(
      state => state.isRefreshingScenarios ?? false,
    )
    this.refreshScenariosError = feature.state.project(
      state => state.refreshScenariosError,
    )
    this.getOtherSectionsForScenario = feature.createSelectorMethod(
      (scenario: ents.core.admin.Scenario) =>
        feature.createSelector(() =>
          store.selectors.Selector.always(
            models.Section.otherSectionsFrom(scenario.visibility),
          ),
        ),
    )
  }
}
